import { MailCheck, MailWarning } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Signup → email verification step of the onboarding flow. Firebase owns the
 * verification state; the portal only surfaces it and offers a resend.
 */
export function EmailVerificationNotice() {
  const { user, emailVerified, resendVerification } = useAuth();
  const [sending, setSending] = useState(false);

  if (!user) return null;

  if (emailVerified) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-600/25 bg-emerald-500/5 px-4 py-3">
        <MailCheck className="size-4 shrink-0 text-emerald-600" />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{user.email}</span> is verified.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
      <MailWarning className="size-4 shrink-0 text-amber-600" />
      <p className="min-w-0 flex-1 text-sm text-muted-foreground">
        Verify <span className="font-medium text-foreground">{user.email}</span> to finish setting
        up your account. You can keep filling in your profile meanwhile.
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={sending}
        onClick={async () => {
          setSending(true);
          try {
            await resendVerification();
            toast.success("Verification email sent.");
          } catch {
            toast.error("We couldn't send the email. Try again in a moment.");
          } finally {
            setSending(false);
          }
        }}
      >
        {sending ? "Sending…" : "Resend email"}
      </Button>
    </div>
  );
}
